import { isHtml } from './check';

function isHtmlElement(elem) {
  return elem && elem instanceof HTMLElement;
}

/**
 * 获取元素计算后的样式属性值
 * @param {HTMLElement} elm
 * @param {string} attrName
 * @return {string}
 */
export function getCurStyleValue(elm, attrName) {
  return window.getComputedStyle(elm).getPropertyValue(attrName);
}

/**
 * 设置/获取元素的attrName对应属性的样式值
 * @param {HTMLElement} elm 要修改/获取样式的元素
 * @param {string} attrName 要修改/获取的样式名
 * @param value  值, 有value就修改, 没有就取值
 * @return {string}
 */
export function css(elm, attrName, value) {
  if (value) {
    elm.style.setProperty(attrName, value);
  } else {
    return getCurStyleValue(elm, attrName);
  }
}

/**
 * 获取元素相对容器的便宜对象
 * @param {*} element 
 * @param {*} container 
 */
export function getOffset(element, container) {
  const DEFAULT_OFFSET = { top: 0, left: 0 };
  if (!element) {
    return DEFAULT_OFFSET;
  }

  if (!element.getClientRects().length) {
    return DEFAULT_OFFSET;
  }

  const rect = element.getBoundingClientRect();

  if (rect.width || rect.height) {
    if (container === window) {
      const topELement = document.documentElement || document.body;
      return {
        top: rect.top - topELement.clientTop,
        left: rect.left - topELement.clientLeft,
      }
    }
    return {
      top: rect.top - container.getBoundingClientRect().top,
      left: rect.left - container.getBoundingClientRect().left,
    }
  }
  return {
    top: rect.top,
    left: rect.left,
  };
};
export function contains(root, n) {
  let node = n;
  while (node) {
    if (node === root) {
      return true;
    }
    node = node.parentNode;
  }

  return false;
}

export function triggerEvent(el, type) {
  if ('createEvent' in document) {
    // modern browsers, IE9+
    const e = document.createEvent('HTMLEvents');
    e.initEvent(type, false, true);
    el.dispatchEvent(e);
  }
}

class ClassManager {
  constructor(elm) {
    this.elm = elm;
    // 兼容方案, 如果浏览器支持classList就用 classList的API, 不支持就自己写
    this.list = elm.classList;
  }

  getClasses() {
    const className = this.elm.getAttribute('class') || '';
    // 去空 排重
    return Array.from(new Set(className.split(/\s+/).filter(k => !!k)));
  }

  toggle(className) {
    if (this.list) {
      this.list.toggle(className);
      return;
    }

    const classes = this.getClasses();
    const index = classes.indexOf(className);
    if (index > -1) {
      classes.splice(index, 1);
    } else {
      classes.push(className);
    }
    this.elm.className = classes.join(' ');
  }

  add(className) {
    if (this.list) {
      this.list.add(className);
      return
    }
    const classes = this.getClasses();
    if (!classes.includes(className)) {
      classes.push(className)
      this.elm.className = classes.join(' ');
    }
  }

  remove(className) {
    if (this.list) {
      this.list.remove(className);
      return
    }
    const classes = this.getClasses();
    const index = classes.indexOf(className);
    if (index > -1) {
      classes.splice(index, 1);
      this.elm.className = classes.join(' ');
    }
  }
}

/**
 * 将一段HTML字符串放到一个代码片段中, 并返回
 * @param {string} htmlStr
 * @return {DocumentFragment}
 */
export function fragment(htmlStr) {
  const fragmentElm = document.createDocumentFragment();
  const div = document.createElement('div');
  div.innerHTML = htmlStr;
  Array.from(div.childNodes).forEach(function (e) { return fragmentElm.appendChild(e); });
  div.remove();
  return fragmentElm;
}

class DomOperator {
  constructor(elm) {
    this.dom = elm
    this.clsMgr = new ClassManager(elm);
    this.handlers = {};
  }

  find = (selector) => {
    return this.dom.querySelector(selector);
  }

  findAll = (selector) => {
    return this.dom.querySelectorAll(selector);
  }

  append = (elem) => {
    if (isHtml(elem)) {
      this.dom.appendChild(fragment(elem));
    } else if (isHtmlElement(elem)) {
      this.dom.appendChild(elem);
    } else {
      console.warn('elem 不是一个有效的html元素, 或html字符串');
    }
  }

  addClass = (clsName) => {
    this.clsMgr.add(clsName);
  }

  removeClass = (clsName) => {
    this.clsMgr.remove(clsName);
  }

  hasClass = (clsName) => {
    return this.clsMgr.getClasses().includes(clsName);
  }

  toggleClass = (clsName) => {
    this.clsMgr.toggle(clsName);
  }

  css = (name, value) => {
    return css(this.dom, name, value)
  }

  attr = (name, value) => {
    if (value === undefined) {
      return this.dom.getAttribute(name);
    }
    this.dom.setAttribute(name, value);
  }

  offset = () => {
    return this.dom.getBoundingClientRect();
  }

  on = (handleName, handle, capture) => {
    const handlers = this.handlers[handleName] || [];
    handlers.push(handle);
    this.handlers[handleName] = handlers;
    this.dom.addEventListener(handleName, handle, capture);
  }

  off = (handleName, handle, capture) => {
    if (handle) {
      this.dom.removeEventListener(handleName, handle, capture);
    } else if (this.handlers[handleName]) {
      this.handlers[handleName].forEach(func => {
        this.dom.removeEventListener(handleName, func, capture);
      });
    }
  }

  trigger = (handleName) => {
    triggerEvent(this.dom, handleName);
  }
}


export const $ = (elm) => {
  if (isHtmlElement(elm)) {
    return new DomOperator(elm);
  }
  throw new Error('Require a DOM element.');
}
